import React, { useCallback, useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import DataTable, {
  CellPrimary,
  CellSecondary,
  ActionButtons,
  EditIcon,
  DeleteIcon,
} from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { DeleteConfirmModal } from 'components/FormModal';
import PageHeaderIcon from 'components/PageHeaderIcon';
import SearchInput from 'components/SearchInput';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { AppContext } from 'shared/AppContext';
import { formatPhoneNumber } from 'shared/formatters';
import PermissionModal from '../PermissionModal/PermissionModal';
import { useGetEmployees, useDeleteEmployee } from './gql/useEmployeeMutations';

// Page Container
const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.background || '#0F172A'
      : '#FFFFFF'};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Header components
const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin: 0;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
`;

// Content wrapper
const ContentWrapper = styled.div`
  padding: 32px;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
  width: 100%;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Page Header Section
const PageHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const EmployeeName = styled.span`
  color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  font-weight: 600;
`;

// Mobile action button styled component
const MobileActionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.1)'};
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white'};
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.05)'};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TrackEmployees = () => {
  const [deleteEmployeeData, setDeleteEmployeeData] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  const { pathname } = useLocation();

  // Context-aware routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const {
    state: { capabilities },
  } = useContext(AppContext);

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Fetch employees data
  const { data, loading } = useGetEmployees();
  const { deleteEmployee, loading: deleteLoading } = useDeleteEmployee();

  const employees = data?.getEmployeesV2?.results || [];
  const count = data?.getEmployeesV2?.count || 0;

  // Handle search
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(count / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // Handle add employee button
  const handleAddEmployee = () => {
    if (!capabilities || capabilities?.add_staff) {
      window.location.href = `${routePrefix}/employees/addEmployee`;
    } else {
      setShowPermissionModal(true);
    }
  };

  // Handle delete employee
  const handleDeleteEmployee = async () => {
    if (!deleteEmployeeData) return;

    const response = await deleteEmployee(deleteEmployeeData.id);

    if (!response || response.errors) {
      toast.error('Error deleting staff member');
    } else {
      toast.success('Staff member successfully deleted');
    }

    setDeleteEmployeeData(null);
  };

  // Handle delete click with permission check
  const handleDeleteClick = (employee) => {
    if (!capabilities || capabilities?.add_staff) {
      setDeleteEmployeeData(employee);
    } else {
      setShowPermissionModal(true);
    }
  };

  // DataTable columns configuration
  const columns = [
    {
      field: 'formatted_name',
      header: 'Name',
      mobileTitle: true,
      render: (employee) => (
        <EmployeeName>
          <CellPrimary>{employee.formatted_name}</CellPrimary>
        </EmployeeName>
      ),
    },
    {
      field: 'email',
      header: 'Email',
      mobileSubtitle: true,
      render: (employee) => <CellSecondary>{employee.email}</CellSecondary>,
    },
    {
      field: 'cellphone',
      header: 'Phone',
      render: (employee) => (
        <CellSecondary>
          {formatPhoneNumber(employee.cellphone) || '-'}
        </CellSecondary>
      ),
    },
  ];

  // Custom actions renderer for desktop
  const actionsRenderer = (employee) => (
    <ActionButtons>
      <ButtonV2
        variant="icon"
        icon={<EditIcon />}
        title="Edit Staff"
        as={Link}
        to={`${routePrefix}/employees/editEmployee/${employee.id}`}
        onClick={(e) => {
          if (capabilities && !capabilities?.add_staff) {
            e.preventDefault();
            setShowPermissionModal(true);
          }
        }}
      />
      <ButtonV2
        variant="icon"
        icon={<DeleteIcon />}
        title="Delete Staff"
        onClick={() => handleDeleteClick(employee)}
      />
    </ActionButtons>
  );

  // Mobile actions renderer
  const mobileActionsRenderer = (employee) => (
    <>
      <MobileActionBtn
        as={Link}
        to={`${routePrefix}/employees/editEmployee/${employee.id}`}
        onClick={(e) => {
          if (capabilities && !capabilities?.add_staff) {
            e.preventDefault();
            setShowPermissionModal(true);
          }
        }}
      >
        <EditIcon />
        Edit
      </MobileActionBtn>
      <MobileActionBtn onClick={() => handleDeleteClick(employee)}>
        <DeleteIcon />
        Delete
      </MobileActionBtn>
    </>
  );

  const totalPages = Math.ceil(count / 15);

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="users" />
            <HeaderTitle>Staff</HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Content Card */}
        <Card noPadding>
          <ContentWrapper>
            {/* Page Header with Search */}
            <PageHeader>
              <SearchInput
                onSearch={handleSearch}
                placeholder="Search staff..."
                debounceMs={500}
              />
              <ButtonV2
                onClick={handleAddEmployee}
                style={{ width: 'auto', flexShrink: 0 }}
              >
                Add Staff
              </ButtonV2>
            </PageHeader>

            {/* DataTable */}
            <DataTable
              columns={columns}
              data={employees}
              loading={loading}
              keyField="id"
              emptyMessage="No staff members found"
              emptySubtext="Add your first staff member to get started"
              actionsRenderer={actionsRenderer}
              mobileActionsRenderer={mobileActionsRenderer}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </ContentWrapper>
        </Card>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={!!deleteEmployeeData}
          onClose={() => setDeleteEmployeeData(null)}
          title="Delete Staff Member"
          message={`Are you sure you want to delete "${deleteEmployeeData?.formatted_name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDeleteEmployee}
          isDeleting={deleteLoading}
        />

        {/* Permission Modal */}
        <PermissionModal
          isVisible={showPermissionModal}
          hideModal={() => setShowPermissionModal(false)}
          link={`${routePrefix}/settings/1`}
        />
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TrackEmployees;
